import { useFeatureGate, useStatsigClient } from '@statsig/react-bindings';
import { createLogger } from '../utils/logger-ssr-safe';

const logger = createLogger({ module: 'useFeatureFlag' });

// Map legacy flag names to Statsig feature gate names
const FLAG_NAME_MAPPING: Record<string, string> = {
  'notification_infrastructure_enabled': 'unified_notification_system',
  'bell_notifications_enabled': 'bell_notifications',
  'banner_notifications_enabled': 'banner_notifications',
  'notification_system_complete': 'notification_system_complete',
  'real_time_notifications': 'real_time_notifications',
};

// Default values for flags when Statsig is not ready or gate doesn't exist
// These should match Statsig's intended 100% rollout gates
const DEFAULT_FLAG_VALUES: Record<string, boolean> = {
  'notification_infrastructure_enabled': true,
  'bell_notifications_enabled': true,
  'banner_notifications_enabled': true,
  'notification_system_complete': false,
  'real_time_notifications': false,
};

interface UseFeatureFlagResult {
  isEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useFeatureFlag(flagName: string): UseFeatureFlagResult {
  // Map legacy flag name to Statsig gate name
  const statsigGateName = FLAG_NAME_MAPPING[flagName] || flagName;

  // Check if Statsig client is available
  const statsigClient = useStatsigClient();

  // Use Statsig's useFeatureGate hook directly
  const gate = useFeatureGate(statsigGateName);

  // Get the default value for this flag
  const defaultValue = DEFAULT_FLAG_VALUES[flagName] ?? false;

  // Determine if we should use default value
  // Use default if:
  // 1. Statsig client isn't available yet
  // 2. OR gate.value is false AND this flag has a default of true
  //    (handles localMode where all gates return false by default)
  const shouldUseDefault = !statsigClient ||
    (gate.value === false && defaultValue === true);

  const isEnabled = shouldUseDefault ? defaultValue : gate.value;

  // Log for debugging
  logger.debug('Feature flag check', {
    flagName,
    statsigGateName,
    statsigValue: gate.value,
    defaultValue,
    isEnabled,
    usingDefault: shouldUseDefault,
    hasStatsigClient: !!statsigClient
  });

  return {
    isEnabled,
    isLoading: false,
    error: null
  };
}
